import { safeLocalStorage } from "@helpers/browser";
import { isBannedProduct } from "@helpers/product";
import DISCOUNT_TYPES from "@constants/DiscountTypes";
import GIFT_PACKAGES, { GIFT_PACKAGES_HANDLES } from "@constants/GiftPackages";

const { getShopifyStoreByCountry } = require("../../locale-shopifies");

const FREE_GIFT_ATTRIBUTE_KEY = "_free_gift";
const LUXE_PRODUCT_HANDLE = "al-luxe";

export function getShopifyCartIdStorageKey(countryCode) {
  return `${getShopifyStoreByCountry(countryCode).name}-shopify_cart_id`;
}

export function getCartId(countryCode) {
  return safeLocalStorage.getItem(getShopifyCartIdStorageKey(countryCode));
}

// Ensure lines have some default values
export function normalizeLines(lines = []) {
  return lines.map((line) => ({
    ...line,
    quantity: Number.isNaN(line.quantity) ? 1 : line.quantity,
    attributes: line.attributes || [],
    variant: line.variant || {},
  }));
}

/**
 * Get discounted items from cart lines based on discount match type and tags.
 * @param {Object} discount
 * @param {Object[]} cartLines
 * @returns {Object[]} filtered cart lines
 * @example
 * const giftDiscountInfo = discountsInfo.find(
 *   ({ type }) => type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE
 * );
 * const giftDiscountedItems = getDiscountedItems(giftDiscountInfo, lineItems);
 */
export function getDiscountedItems(discount, cartLines) {
  if (!discount || !cartLines?.length) return [];

  const { matchType, matchTags = [] } = discount;

  return cartLines.filter((line) => {
    const productTags = line.variant?.product?.tags || [];
    const attributes = line.attributes || [];
    const isFreeGift = attributes.some(
      ({ key, value }) => key === "_free_gift" && value === "true"
    );

    if (isFreeGift) return false; // filter out free gift items whatever the discount type
    if (matchType === "include") {
      return productTags.some((tag) => matchTags.includes(tag));
    }
    if (matchType === "exclude") {
      return !productTags.some((tag) => matchTags.includes(tag));
    }
    return false;
  });
}

export function separateLineItems(items, attributeValue) {
  return items.reduce(
    (prev, current) => {
      const attributeObject = current?.attributes?.find(
        (attribute) => attribute.key === attributeValue
      );
      if (attributeObject) {
        prev.customItems.push({
          ...current,
          [attributeValue]: attributeObject.value,
        });
      } else {
        prev.regularItems.push(current);
      }

      return prev;
    },
    { customItems: [], regularItems: [] }
  );
}

export const getEligibleCartLines = ({
  lines = [],
  discountsToCheck = null,
  excludeLuxeItems = true,
}) =>
  lines.filter((line) => {
    const tags = line?.variant?.product?.tags || [];
    const isBannedByTags = discountsToCheck ? isBannedProduct(tags, discountsToCheck) : false;

    const attributes = line?.attributes || [];
    const isFreeGift = attributes.some(
      ({ key, value }) => key === FREE_GIFT_ATTRIBUTE_KEY && value === "true"
    );
    const isLuxeItem = line?.variant?.product?.handle === LUXE_PRODUCT_HANDLE;

    return !isBannedByTags && !isFreeGift && (!excludeLuxeItems || !isLuxeItem);
  });

export const getEligibleAmount = (eligibleLines = []) =>
  eligibleLines.reduce((total, line) => total + (line?.variant?.full_price || 0), 0);

export const getEligibleCartMetrics = ({
  lines = [],
  discountsToCheck = null,
  excludeLuxeItems = true,
}) => {
  const eligibleLines = getEligibleCartLines({
    lines,
    discountsToCheck,
    excludeLuxeItems,
  });
  const eligibleAmount = getEligibleAmount(eligibleLines);
  const eligibleQuantity = eligibleLines.reduce((sum, line) => sum + (line.quantity || 0), 0);
  const isQuantityTieredDiscount = discountsToCheck?.some(
    ({ type }) => type === DISCOUNT_TYPES.QUANTITY_PERCENT
  );
  const currentEligibleValue = isQuantityTieredDiscount ? eligibleQuantity : eligibleAmount;

  return {
    eligibleLines,
    currentEligibleValue,
  };
};

/**
 * Get the tiered discount percent that would apply if an item were added to the cart.
 * @param {Object} params
 * @param {Object} params.tieredDiscount - discount config with a `discounts` array of [threshold, percent] pairs
 * @param {number} [params.eligibleValue=0] - current eligible cart value (amount or quantity)
 * @param {number} params.itemFullPrice - full price of the item being considered
 * @returns {number} discount percent for the tier reached after adding the item, or 0 if none
 */
export function getUpsellTierDiscounts({ tieredDiscount, eligibleValue = 0, itemFullPrice } = {}) {
  if (!tieredDiscount?.discounts?.length || itemFullPrice == null) {
    return 0;
  }

  const projectedValue = eligibleValue + itemFullPrice;
  return tieredDiscount.discounts.reduce(
    (percent, [threshold, tierPercent]) => (threshold <= projectedValue ? tierPercent : percent),
    0
  );
}

export const isMTOOnlyCart = (cart) => {
  /**
   * Exclude:
   * 1. Free Gifts
   * 2. Luxe item
   * 3. Gift Packages
   */

  const filteredCart = cart.lines.filter(
    (line) =>
      !line.attributes.some(({ key, value }) => key === "_free_gift" && value === "true") &&
      line.variant.sku !== "L-0001" &&
      !GIFT_PACKAGES_HANDLES.includes(line.variant.product.handle)
  );
  return (
    filteredCart.length > 0 &&
    filteredCart.every((line) =>
      line.attributes.some((attr) => attr.key === "_mto" && attr.value === "true")
    )
  );
};
